import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthStore } from '../../../store/auth.store';
import { ValidationUtils } from '../../../utils/validation.utils';
import { FormErrors, RegisterDTO } from '../../../types/auth.types';
import {
  ErrorAlertComponent,
  TextInputComponent,
  LoadingSkeletonComponent,
} from '@sandbox-platform/shared-ui';
import {
  HeaderComponent,
  SubmitSignupButtonComponent,
  RegistrationSuccessPaneComponent,
} from './components';
import { AuthBannerComponent } from '../components';

@Component({
  selector: 'app-register',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    ErrorAlertComponent,
    TextInputComponent,
    LoadingSkeletonComponent,
    HeaderComponent,
    SubmitSignupButtonComponent,
    RegistrationSuccessPaneComponent,
    AuthBannerComponent
],
  templateUrl: './register.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'animate-fadeIn',
  },
})
export class RegisterComponent implements OnInit {
  readonly authStore = inject(AuthStore);
  private router = inject(Router);

  // Component loading state
  isComponentReady = signal<boolean>(false);

  // Form data
  formData = signal<RegisterDTO>({
    email: '',
    password: '',
    confirm_password: '',
    first_name: '',
    last_name: '',
    username: '',
  });

  // Form validation errors
  formErrors = signal<FormErrors>({});

  // Registration success state
  registrationSuccess = signal<boolean>(false);

  constructor() {
    // Clear any existing errors when component loads
    this.authStore.clearError();
  }

  ngOnInit(): void {
    // Simulate component initialization and smooth loading
    setTimeout(() => {
      this.isComponentReady.set(true);
    }, 50);
  }

  validateField(fieldName: keyof RegisterDTO): void {
    const data = this.formData();
    const errors = { ...this.formErrors() };

    switch (fieldName) {
      case 'email':
        const emailError = ValidationUtils.validateEmail(data.email);
        if (emailError) {
          errors[fieldName] = emailError;
        } else {
          delete errors[fieldName];
        }
        break;
      case 'password':
        const passwordError = ValidationUtils.validatePassword(data.password);
        if (passwordError) {
          errors[fieldName] = passwordError;
        } else {
          delete errors[fieldName];
        }
        // Revalidate confirm password if it has been filled
        if (data.confirm_password) {
          this.validateField('confirm_password');
        }
        break;
      case 'confirm_password':
        const confirmPasswordError = ValidationUtils.validateConfirmPassword(
          data.password,
          data.confirm_password
        );
        if (confirmPasswordError) {
          errors[fieldName] = confirmPasswordError;
        } else {
          delete errors[fieldName];
        }
        break;
      case 'first_name':
        const firstNameError = ValidationUtils.validateRequired(
          data.first_name,
          'First name'
        );
        if (firstNameError) {
          errors[fieldName] = firstNameError;
        } else {
          delete errors[fieldName];
        }
        break;
      case 'last_name':
        const lastNameError = ValidationUtils.validateRequired(
          data.last_name,
          'Last name'
        );
        if (lastNameError) {
          errors[fieldName] = lastNameError;
        } else {
          delete errors[fieldName];
        }
        break;
      case 'username':
        const usernameError = ValidationUtils.validateUsername(
          data.username || ''
        );
        if (usernameError) {
          errors[fieldName] = usernameError;
        } else {
          delete errors[fieldName];
        }
        break;
    }

    this.formErrors.set(errors);
  }

  validateForm(): FormErrors {
    const data = this.formData();
    return ValidationUtils.validateRegisterForm(
      data.email,
      data.password,
      data.confirm_password,
      data.first_name,
      data.last_name,
      data.username
    );
  }

  isFormValid(): boolean {
    const data = this.formData();
    const errors = this.validateForm();
    return (
      !ValidationUtils.hasErrors(errors) &&
      data.email.length > 0 &&
      data.password.length > 0 &&
      data.first_name.length > 0 &&
      data.last_name.length > 0
    );
  }

  async onSubmit(): Promise<void> {
    if (!this.isFormValid()) {
      this.formErrors.set(this.validateForm());
      return;
    }

    try {
      const success = await this.authStore.register(this.formData());

      if (success) {
        this.registrationSuccess.set(true);
      }
    } catch (error) {
      // Error handling is done in the store
      console.error('Registration error:', error);
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }
}
